import axios from 'axios';
import Constants from '../Constants';
import AppConfig from '../AppConfig';
import { postRequest } from '../utils/axiosClient';
import { getAuthtoken } from '../utils/helper';
import {withRetry } from '@demandproai/shared';
import { resolveFileMimeType } from '../utils/imagePreview';
import { removeCaseDataFiles } from '../redux/actions/fileUpload';

class UploadManager {
    constructor() {
        if (UploadManager.instance) {
            return UploadManager.instance;
        }

        this.store = null;
        this.activeControllers = new Map(); // Track AbortControllers for cancellation
        this.maxConcurrentUploads = 3; // Max parallel uploads
        this.currentUploads = 0;
        this.processingQueue = false;

        UploadManager.instance = this;
    }

    // Initialize with Redux store
    setStore(store) {
        this.store = store;
    }

    // Start processing the upload queue
    async processQueue() {
        if (!this.store || this.processingQueue) return;

        this.processingQueue = true;

        while (true) {
            const state = this.store.getState();
            const { uploadQueue } = state.fileUpload;

            // Check if we can process more uploads
            if (this.currentUploads >= this.maxConcurrentUploads || uploadQueue.length === 0) {
                this.processingQueue = false;
                break;
            }

            // Get next file from queue
            const nextFile = uploadQueue[0];
            if (nextFile) {
                this.currentUploads++;
                this.uploadFile(nextFile).finally(() => {
                    this.currentUploads--;
                    this.processQueue(); // Process next item
                });
            }

            // Small delay to prevent tight loop
            await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    // Upload a single file
    async uploadFile(fileData) {
        const { id, file, fileName, caseId, caseRecordType, presignedUrl, uniqueKey, callback, metaData } = fileData;
        
        try {
            // Dispatch start action
            this.store.dispatch({
                type: 'FILE_UPLOAD_START',
                payload: { uploadId: id },
            });

            let signedUrl = presignedUrl;
            let fileUniqueKey = uniqueKey;

            if (!signedUrl || !fileUniqueKey) {
               throw new Error('Failed to get presigned URL');
            }

            // Step 2: Upload to S3 with progress tracking
            await this.uploadToS3(id, signedUrl, file);
            if (caseRecordType) {
                await this.dedicateFile(
                    fileUniqueKey,
                    caseRecordType,
                    caseId,
                    metaData
            );
            }

            if (typeof callback === 'function') {
                await callback({
                    caseId,
                    fileName,
                    uniqueKey: fileUniqueKey,
                    s3UrlPath: fileUniqueKey,
                    metaData,
                });
                this.store.dispatch(removeCaseDataFiles(fileUniqueKey));
            }

            // Success
            this.store.dispatch({
                type: 'FILE_UPLOAD_SUCCESS',
                payload: {
                    uploadId: id,
                    result: {
                        uniqueKey: fileUniqueKey,
                        s3UrlPath: fileUniqueKey,
                        fileName,
                    },
                },
            });

            return { uniqueKey: fileUniqueKey, signedUrl };
        } catch (error) {
            console.error(`Upload failed for ${fileName}:`, error);

            // Check if it was cancelled
            if (axios.isCancel(error)) {
                console.log(`Upload cancelled for ${fileName}`);
                return;
            }

            // Dispatch failure
            this.store.dispatch({
                type: 'FILE_UPLOAD_FAILED',
                payload: {
                    uploadId: id,
                    error: error.message || 'Upload failed',
                },
            });

            throw error;
        } finally {
            // Cleanup abort controller
            this.activeControllers.delete(id);
        }
    }

    // Get presigned URL from API (using existing axiosClient with interceptors)
    async getPresignedUrl(file, fileName, caseId, metaData) {
        try {
            // Stringify metadata
            const stringifiedMetaData = {};
            for (const key in metaData) {
                stringifiedMetaData[key] = typeof metaData[key] === 'string'
                    ? metaData[key]
                    : JSON.stringify(metaData[key]);
            }

            stringifiedMetaData.originalFileName = fileName;

            const response = await postRequest({
                url: Constants.ApiUrl.preSignedUrls,
                data: {
                    fileName,
                    fileType: resolveFileMimeType(file),
                    caseId,
                    metaData: stringifiedMetaData,
                },
            });

            if (response?.status === 200) {
                return response.data;
            }

            return null;
        } catch (error) {
            console.error('Error getting presigned URL:', error);
            throw error;
        }
    }

    // Upload file to S3 with progress tracking
    async uploadToS3(uploadId, signedUrl, file) {
        // Create abort controller for this upload
        const controller = new AbortController();
        this.activeControllers.set(uploadId, controller);

        const uploadWithProgress = async () => {
            return axios.put(signedUrl, file, {
                headers: { 'Content-Type': resolveFileMimeType(file) },
                signal: controller.signal,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );

                    // Dispatch progress update
                    if (this.store) {
                        this.store.dispatch({
                            type: 'FILE_UPLOAD_UPDATE_PROGRESS',
                            payload: {
                                uploadId,
                                progress: percentCompleted,
                            },
                        });
                    }
                },
            });
        };

        // Retry on failure
        const res = await withRetry(uploadWithProgress);

        if (res.status !== 200) {
            throw new Error('Upload failed with status ' + res.status);
        }

        return res;
    }

    // Dedicate file to case (call PFQ API)
    async dedicateFile(uniqueKey, caseRecordType, caseId, metaData) {
        const dedicateWithRetry = async () => {
            const token = getAuthtoken();
 
            const response = await axios.post(
                `${AppConfig.PfqBaseUrl}${Constants.ApiUrl.addDedicatedFile}`,
                {
                    fileS3UniqueName: uniqueKey,
                    caseRecordType,
                    caseId,
                    metaData
                },
                {
                    headers: {
                        'Authorization': token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true,
                }
            );

            // Accept both 200 and 201 status codes (201 = Created)
            if (response?.status === 200 || response?.status === 201) {
                return response.data;
            }

            throw new Error(`Failed to dedicate file. Status: ${response?.status}`);
        };

        return withRetry(dedicateWithRetry);
    }

    // Cancel an upload
    cancelUpload(uploadId) {
        const controller = this.activeControllers.get(uploadId);
        if (controller) {
            controller.abort();
            this.activeControllers.delete(uploadId);
        }

        if (this.store) {
            this.store.dispatch({
                type: 'FILE_UPLOAD_CANCEL',
                payload: { uploadId },
            });
        }
    }

    // Retry a failed upload
    retryUpload(uploadId) {
        if (this.store) {
            this.store.dispatch({
                type: 'FILE_UPLOAD_RETRY',
                payload: { uploadId },
            });

            // Process queue again
            this.processQueue();
        }
    }
}

// Export singleton instance
const uploadManagerInstance = new UploadManager();
export default uploadManagerInstance;
